import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  captureLoginFailed,
  readAuthUrlFailure,
} from '../../utils/authProductEvents';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const posthog = usePostHog();

  console.log('AuthCallback: Component mounted');
  console.log('AuthCallback: Current URL:', window.location.href);
  console.log('AuthCallback: Search params:', Object.fromEntries(searchParams));
  console.log('AuthCallback: Auth state:', { isAuthenticated });

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('AuthCallback: Starting simplified auth callback flow');

        const urlFailure = readAuthUrlFailure();
        if (urlFailure) {
          captureLoginFailed(posthog, urlFailure);
          setError(t('authenticationFailed', { ns: 'auth' }));
          return;
        }

        // Get the session from Supabase (handles URL hash/fragment automatically)
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('AuthCallback: Supabase auth error:', error);
          captureLoginFailed(posthog, error);
          setError(t('authenticationFailed', { ns: 'auth' }));
          return;
        }

        if (data.session) {
          console.log('AuthCallback: Authentication successful, redirecting to /authenticate');
          
          // Always redirect to /authenticate with any URL params preserved
          // The /authenticate route will handle all OAuth flow logic
          const authenticateUrl = searchParams.toString() 
            ? `/authenticate?${searchParams.toString()}`
            : '/authenticate';
          
          console.log('AuthCallback: Redirecting to:', authenticateUrl);
          navigate(authenticateUrl, { replace: true });
          
        } else {
          // No session found, redirect to login
          console.log('AuthCallback: No session found, redirecting to login');
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('AuthCallback: Unexpected error during auth callback:', err);
        captureLoginFailed(posthog, err);
        setError(t('unexpectedError', { ns: 'auth' }));
      }
    };

    // Handle the auth callback
    handleAuthCallback();
  }, [navigate, posthog, searchParams, t]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full space-y-8 p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {t('authenticationError', { ns: 'auth' })}
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md transition-colors"
            >
              {t('backToLogin', { ns: 'auth' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {t('completingAuthentication', { ns: 'auth' })}
          </h2>
          <p className="text-muted-foreground">
            {t('pleaseWait', { ns: 'auth' })}
          </p>
        </div>
      </div>
    </div>
  );
}
